// src\pages\StaffDashboard.js

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../styles/StaffDashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import timeAgo from "../utils/timeAgo";

const StaffDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]); // For user's uploaded photos
  const [photoError, setPhotoError] = useState(""); // For photo fetch errors
  const [currentPage, setCurrentPage] = useState(0); // Pagination page
  const [hasMorePhotos, setHasMorePhotos] = useState(true); // Track if more photos exist
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false); // Loading state for photos
  const [editingPhotoId, setEditingPhotoId] = useState(null); // Track which photo is being edited
  const [newStory, setNewStory] = useState(""); // Store the edited story
  const observer = useRef(null); // For IntersectionObserver

  const token = localStorage.getItem("token");

  // Fetch profile
  const profileLoadedRef = useRef(false);
  useEffect(() => {
    if (profileLoadedRef.current) return;
    profileLoadedRef.current = true;
    const fetchProfile = async () => {
      try {
        const response = await axios.get("https://ghrianghraffy.com/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(response.data);
        setNewDisplayName(response.data.display_name || "");
      } catch (err) {
        console.error("Error fetching staff profile:", err);
        setProfileError("Failed to load profile. Please try again.");
      }
    };
    if (token) fetchProfile();
  }, [token]);

  // Fetch user's approved photos
  const fetchPhotos = async (page) => {
    if (!hasMorePhotos || isLoadingPhotos) return;
    setIsLoadingPhotos(true);
    try {
      const response = await axios.get("https://ghrianghraffy.com/photos/view-photos", {
        headers: { Authorization: `Bearer ${token}` },
        params: { my_photos: true, offset: page * 20, limit: 20 },
      });
      const newPhotos = response.data;
      setPhotos((prev) => (page === 0 ? newPhotos : [...prev, ...newPhotos]));
      setHasMorePhotos(newPhotos.length === 20); // More photos if we got a full batch
    } catch (err) {
      console.error("Error fetching photos:", err);
      setPhotoError("Failed to load your photos. Please try again.");
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  // Fetch photos on mount and page change
  const initialPhotosLoadedRef = useRef(false);
  useEffect(() => {
    if (!token) return;
    // Avoid double-load of the first page in React StrictMode (dev)
    if (currentPage === 0) {
      if (initialPhotosLoadedRef.current) return;
      initialPhotosLoadedRef.current = true;
    }
    fetchPhotos(currentPage);
  }, [token, currentPage]);

  // Set up IntersectionObserver for infinite scroll
  const setLastPhotoRef = (node) => {
    if (observer.current) observer.current.disconnect();
    if (node && hasMorePhotos && !isLoadingPhotos) {
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setCurrentPage((prev) => prev + 1);
          }
        },
        { threshold: 0.5 }
      );
      observer.current.observe(node);
    }
  };

  // Update display name
  const handleUpdateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setError("Display name cannot be empty.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        "https://ghrianghraffy.com/auth/update-display-name",
        { new_display_name: newDisplayName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile((prev) => ({ ...prev, display_name: newDisplayName }));
      setEditingDisplayName(false);
      setMessage("Display name updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update display name.");
    } finally {
      setLoading(false);
    }
  };

  // Start editing a photo's story
  const handleEditStory = (photo) => {
    setEditingPhotoId(photo.id);
    setNewStory(photo.story || "");
    setError(""); // Clear any existing errors
  };

  // Cancel editing a photo's story
  const handleCancelEdit = () => {
    setEditingPhotoId(null);
    setNewStory("");
    setError("");
  };

  // Update a photo's story
  const handleUpdateStory = async (photoId) => {
    if (!newStory.trim()) {
      setError("Story cannot be empty.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `https://ghrianghraffy.com/photos/${photoId}/update-story`,
        { new_story: newStory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos((prev) =>
        prev.map((photo) =>
          photo.id === photoId ? { ...photo, story: newStory } : photo
        )
      );
      setEditingPhotoId(null);
      setNewStory("");
      setMessage("Story updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update story.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="staff-dashboard">
      <h1>Staff Dashboard</h1>

      {/* Profile Section */}
      <section className="profile-section">
        <h2>Your Profile</h2>
        {profileError && <p className="error">{profileError}</p>}
        {profile ? (
          <div className="profile-card">
            <p>
              <strong>Display Name:</strong>{" "}
              {editingDisplayName ? (
                <>
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    maxLength={255}
                    style={{ marginRight: "10px" }}
                  />
                  <button onClick={handleUpdateDisplayName} disabled={loading}>
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingDisplayName(false);
                      setNewDisplayName(profile.display_name || "");
                    }}
                    style={{ marginLeft: "5px" }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {profile.display_name || profile.email}{" "}
                  {profile.display_name === profile.email.split("@")[0] && (
                    <button
                      onClick={() => setEditingDisplayName(true)}
                      style={{
                        fontSize: "12px",
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      Click to Change your Display Name
                      <span
                        className="nudge-arrow"
                        title="Click here to personalise your display name!"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </span>
                    </button>
                  )}
                </>
              )}
            </p>
            <p>
              <strong>Email:</strong> {profile.email}
            </p>
            <p>
              <strong>Role:</strong> {profile.role}
            </p>
            <p>
              <strong>School:</strong> {profile.school_name || "No school assigned"}
            </p>
          </div>
        ) : (
          <p>Loading profile...</p>
        )}
      </section>

      {/* Your Photos Section */}
      <section className="your-photos-section">
        <h2>Your Uploaded Photos</h2>
        {photoError && <p className="error">{photoError}</p>}
        {photos.length > 0 ? (
          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="photo-card"
                ref={index === photos.length - 1 ? setLastPhotoRef : null}
              >
                <img
                  src={`https://ghrianghraffy.com/${photo.thumbnail_path}`}
                  alt={photo.story || "Photo"}
                  className="photo-thumbnail"
                  loading="lazy"
                />
                {editingPhotoId === photo.id ? (
                  <div className="edit-story-form">
                    <textarea
                      value={newStory}
                      onChange={(e) => setNewStory(e.target.value)}
                      placeholder="Edit story and hashtags"
                      className="form-textarea"
                      maxLength={255}
                    />
                    <button
                      onClick={() => handleUpdateStory(photo.id)}
                      disabled={loading}
                      className="save-story-button"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="cancel-story-button"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <p>{photo.story || "No story provided"}</p>
                    <p>Uploaded {photo.created_at ? timeAgo(photo.created_at) : "N/A"}</p>
                    <button
                      onClick={() => handleEditStory(photo)}
                      className="edit-story-button"
                    >
                      Edit Story
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>No approved photos uploaded yet.</p>
        )}
        {isLoadingPhotos && <p className="loading-indicator">Loading more photos...</p>}
        {!hasMorePhotos && photos.length > 0 && (
          <p className="no-more-photos">No more photos to load.</p>
        )}
      </section>
    </div>
  );
};

export default StaffDashboard;